// Schema.org markup generators for SEO

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businesscapitalguide.net';

export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Business Capital Guide',
    description: 'Educational platform for business funding options and financial guidance',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/918694c0b33b18123a4b53c7a1500734/df4f42b283b7dab6698273cc5e07b1e4.png',
      width: 512,
      height: 512
    },
    foundingDate: '2024',
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    knowsAbout: [
      'Business Funding',
      'Small Business Loans',
      'Working Capital',
      'Equipment Financing',
      'Commercial Real Estate Loans',
      'Business Lines of Credit',
      'Term Loans'
    ],
    slogan: 'Navigate Business Funding with Confidence'
  };
}

export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}

// Homepage specific schema with breadcrumb
export function generateHomepageSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: 'Business Capital Guide | Educational Platform for Business Funding Options',
        description: 'Educational platform helping business owners understand funding options including working capital, lines of credit, term loans, equipment financing, and commercial real estate loans.',
        isPartOf: {
          '@id': `${SITE_URL}/#website`
        },
        about: {
          '@id': `${SITE_URL}/#organization`
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://public.readdy.ai/ai/img_res/7820371803100a7855876040c19e2da1.jpg'
        },
        datePublished: '2024-01-01',
        dateModified: new Date().toISOString().split('T')[0],
        inLanguage: 'en-US'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          }
        ]
      }
    ]
  };
}

// Business Funding page schema
export function generateBusinessFundingPageSchema() {
  const pageUrl = `${SITE_URL}/business-funding`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: 'What is Business Funding? Complete Guide to Business Loans & Financing',
        description: 'Learn everything about business funding: what lenders look for, types of financing, qualification requirements, and how to prepare your business for funding success.',
        isPartOf: {
          '@id': `${SITE_URL}/#website`
        },
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`
        },
        datePublished: '2024-01-01',
        dateModified: new Date().toISOString().split('T')[0],
        inLanguage: 'en-US'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Business Funding Guide',
            item: pageUrl
          }
        ]
      },
      {
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        headline: 'What is Business Funding? Complete Guide to Business Loans & Financing',
        description: 'Comprehensive educational guide about business funding options, lender requirements, and application process.',
        author: {
          '@id': `${SITE_URL}/#organization`
        },
        publisher: {
          '@id': `${SITE_URL}/#organization`
        },
        datePublished: '2024-01-01',
        dateModified: new Date().toISOString().split('T')[0],
        mainEntityOfPage: {
          '@id': `${pageUrl}#webpage`
        },
        articleSection: 'Business Finance Education'
      }
    ]
  };
}

// Cities directory page schema
export function generateCitiesPageSchema() {
  const pageUrl = `${SITE_URL}/cities`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: 'Business Funding by City | Find Local Business Capital Solutions',
        description: 'Explore business funding options in your city. Pre-qualification available for businesses in New York, Miami, Dallas, Houston, Los Angeles, Chicago, Atlanta, Phoenix, Las Vegas, and Tampa.',
        isPartOf: {
          '@id': `${SITE_URL}/#website`
        },
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`
        },
        dateModified: new Date().toISOString().split('T')[0],
        inLanguage: 'en-US'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Cities',
            item: pageUrl
          }
        ]
      }
    ]
  };
}

// Funding type page schema
export function generateFundingTypePageSchema(
  fundingType: string,
  fundingSlug: string,
  description: string
) {
  const pageUrl = `${SITE_URL}/funding/${fundingSlug}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${fundingType} | Business Capital Guide`,
        description: description,
        isPartOf: {
          '@id': `${SITE_URL}/#website`
        },
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`
        },
        dateModified: new Date().toISOString().split('T')[0],
        inLanguage: 'en-US'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Business Funding',
            item: `${SITE_URL}/business-funding`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: fundingType,
            item: pageUrl
          }
        ]
      },
      {
        '@type': 'FinancialProduct',
        '@id': `${pageUrl}#product`,
        name: fundingType,
        description: description,
        provider: {
          '@id': `${SITE_URL}/#organization`
        },
        category: 'Business Financing'
      }
    ]
  };
}

export function generateFinancialServiceSchema(cityName?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: cityName 
      ? `Business Capital Guide - ${cityName}`
      : 'Business Capital Guide',
    description: 'Educational resources and guidance for business funding options',
    serviceType: 'Financial Education',
    areaServed: cityName ? {
      '@type': 'City',
      name: cityName
    } : {
      '@type': 'Country',
      name: 'United States'
    },
    disclaimer: 'Educational platform. Not a lender. No guarantee of approval. Eligibility varies.'
  };
}

export function generateServiceAreaSchema(cityName: string, stateName: string, stateCode: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Business Funding Education',
    provider: {
      '@type': 'Organization',
      name: 'Business Capital Guide'
    },
    areaServed: {
      '@type': 'City',
      name: cityName,
      containedIn: {
        '@type': 'State',
        name: stateName,
        address: {
          '@type': 'PostalAddress',
          addressRegion: stateCode,
          addressCountry: 'US'
        }
      }
    }
  };
}

export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

export function generateWebPageSchema(
  title: string,
  description: string,
  url: string,
  cityName?: string
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Business Capital Guide',
      url: SITE_URL
    },
    ...(cityName && {
      about: {
        '@type': 'Place',
        name: cityName
      }
    })
  };
}

export function generateEducationalWebPageSchema(
  title: string,
  description: string,
  url: string
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    about: {
      '@type': 'FinancialProduct',
      name: 'Business Funding Education',
      description: 'Educational resources about business funding options'
    },
    isPartOf: {
      '@type': 'WebSite',
      name: 'Business Capital Guide',
      url: SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: 'Business Capital Guide',
      url: SITE_URL
    }
  };
}

// LocalBusiness Schema for city pages
export interface LocalBusinessSchemaParams {
  cityName: string;
  stateCode: string;
  stateName: string;
  latitude: string;
  longitude: string;
  url: string;
  description?: string;
  serviceTypes?: string[];
}

export function generateLocalBusinessSchema(params: LocalBusinessSchemaParams) {
  const {
    cityName,
    stateCode,
    stateName,
    latitude,
    longitude,
    url,
    description,
    serviceTypes = [
      'Working Capital Loans',
      'Business Lines of Credit',
      'Term Loans',
      'Equipment Financing',
      'Commercial Real Estate Loans'
    ]
  } = params;

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${url}#localbusiness`,
    name: `Business Capital Guide - ${cityName}`,
    description: description || `Educational platform providing business funding guidance and resources for ${cityName}, ${stateName} businesses. Learn about working capital, term loans, equipment financing, and more.`,
    url: url,
    telephone: '',
    address: {
      '@type': 'PostalAddress',
      addressLocality: cityName,
      addressRegion: stateCode,
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: latitude,
      longitude: longitude
    },
    areaServed: {
      '@type': 'City',
      name: cityName,
      containedInPlace: {
        '@type': 'State',
        name: stateName,
        identifier: `US-${stateCode}`
      }
    },
    priceRange: '$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00'
    },
    sameAs: [
      SITE_URL
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Business Funding Services',
      itemListElement: serviceTypes.map((service, index) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service,
          description: `${service} information and guidance for ${cityName} businesses`
        },
        position: index + 1
      }))
    }
  };
}

// Combined City Page Schema with LocalBusiness + FAQ
export interface CityPageSchemaParams {
  cityName: string;
  stateCode: string;
  stateName: string;
  latitude: string;
  longitude: string;
  url: string;
  faqs: Array<{ question: string; answer: string }>;
  description?: string;
  avgLoanAmount?: string;
  keyIndustries?: string[];
}

export function generateCityPageSchema(params: CityPageSchemaParams) {
  const {
    cityName,
    stateCode,
    stateName,
    latitude,
    longitude,
    url,
    faqs,
    description,
    avgLoanAmount,
    keyIndustries = []
  } = params;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      // WebPage Schema
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url: url,
        name: `Business Funding in ${cityName}, ${stateCode} | Small Business Loans & Capital Solutions`,
        description: description || `Complete guide to business funding options in ${cityName}. Learn what lenders look for, explore funding types, and understand ${cityName}'s business economy.`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${SITE_URL}#website`,
          url: SITE_URL,
          name: 'Business Capital Guide',
          publisher: {
            '@type': 'Organization',
            name: 'Business Capital Guide'
          }
        },
        breadcrumb: {
          '@id': `${url}#breadcrumb`
        },
        datePublished: '2024-01-01',
        dateModified: new Date().toISOString().split('T')[0]
      },
      // BreadcrumbList Schema
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Business Funding',
            item: `${SITE_URL}/business-funding`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: `${cityName}, ${stateCode}`,
            item: url
          }
        ]
      },
      // LocalBusiness Schema
      {
        '@type': 'LocalBusiness',
        '@id': `${url}#localbusiness`,
        name: `Business Capital Guide - ${cityName}`,
        description: `Educational platform providing business funding guidance for ${cityName}, ${stateName} businesses.${avgLoanAmount ? ` Average loan amount: ${avgLoanAmount}.` : ''}`,
        url: url,
        address: {
          '@type': 'PostalAddress',
          addressLocality: cityName,
          addressRegion: stateCode,
          addressCountry: 'US'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: latitude,
          longitude: longitude
        },
        areaServed: {
          '@type': 'City',
          name: cityName,
          containedInPlace: {
            '@type': 'State',
            name: stateName,
            identifier: `US-${stateCode}`
          }
        },
        priceRange: '$$',
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00'
        },
        knowsAbout: [
          'Business Funding',
          'Small Business Loans',
          'Working Capital',
          'Equipment Financing',
          'Commercial Real Estate Loans',
          ...keyIndustries
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Business Funding Information Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Working Capital Information',
                description: `Working capital loan guidance for ${cityName} businesses`
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Term Loan Information',
                description: `Term loan guidance for ${cityName} businesses`
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Equipment Financing Information',
                description: `Equipment financing guidance for ${cityName} businesses`
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Line of Credit Information',
                description: `Business line of credit guidance for ${cityName} businesses`
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Commercial Real Estate Information',
                description: `Commercial real estate loan guidance for ${cityName} businesses`
              }
            }
          ]
        }
      },
      // FAQPage Schema
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      },
      // FinancialService Schema
      {
        '@type': 'FinancialService',
        '@id': `${url}#financialservice`,
        name: `Business Funding Information - ${cityName}`,
        description: `Educational platform providing guidance on business funding options in ${cityName}, ${stateName}`,
        url: url,
        areaServed: {
          '@type': 'City',
          name: cityName,
          containedIn: {
            '@type': 'State',
            name: stateName
          }
        },
        serviceType: [
          'Business Funding Information',
          'Working Capital Loans',
          'Term Loans',
          'Equipment Financing',
          'Line of Credit',
          'Commercial Real Estate Loans'
        ]
      }
    ]
  };
}

// City Funding Page Schema (for specific funding type pages)
export interface CityFundingPageSchemaParams {
  cityName: string;
  stateCode: string;
  stateName: string;
  latitude: string;
  longitude: string;
  url: string;
  fundingType: string;
  fundingTypeSlug: string;
  faqs: Array<{ question: string; answer: string }>;
  description?: string;
}

export function generateCityFundingPageSchema(params: CityFundingPageSchemaParams) {
  const {
    cityName,
    stateCode,
    stateName,
    latitude,
    longitude,
    url,
    fundingType,
    fundingTypeSlug,
    faqs,
    description
  } = params;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      // WebPage Schema
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url: url,
        name: `${fundingType} in ${cityName}, ${stateCode} | Business Funding Guide`,
        description: description || `Learn about ${fundingType.toLowerCase()} options for ${cityName} businesses. Educational resources and guidance.`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${SITE_URL}#website`,
          url: SITE_URL,
          name: 'Business Capital Guide'
        },
        breadcrumb: {
          '@id': `${url}#breadcrumb`
        }
      },
      // BreadcrumbList Schema
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Business Funding',
            item: `${SITE_URL}/business-funding`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: cityName,
            item: `${SITE_URL}/cities/${cityName.toLowerCase().replace(/\s+/g, '-')}`
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: fundingType,
            item: url
          }
        ]
      },
      // LocalBusiness Schema
      {
        '@type': 'LocalBusiness',
        '@id': `${url}#localbusiness`,
        name: `${fundingType} Information - ${cityName}`,
        description: `Educational resources about ${fundingType.toLowerCase()} for businesses in ${cityName}, ${stateName}`,
        url: url,
        address: {
          '@type': 'PostalAddress',
          addressLocality: cityName,
          addressRegion: stateCode,
          addressCountry: 'US'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: latitude,
          longitude: longitude
        },
        areaServed: {
          '@type': 'City',
          name: cityName
        }
      },
      // FinancialService Schema
      {
        '@type': 'FinancialService',
        '@id': `${url}#financialservice`,
        name: `${fundingType} for ${cityName} Businesses`,
        description: `Educational resources about ${fundingType.toLowerCase()} options for businesses in ${cityName}, ${stateName}`,
        serviceType: fundingType,
        areaServed: {
          '@type': 'City',
          name: cityName,
          addressRegion: stateName
        },
        disclaimer: 'Educational platform. Not a lender. No guarantee of approval. Eligibility varies.'
      },
      // FAQPage Schema
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    ]
  };
}
